import React from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { pricingAnalytics } from '@/utils/entities/clients/pricingTracking';
import type { AddOnCatalogItem } from '@/types/addons';

export interface AddOnsSelectorProps {
  items: AddOnCatalogItem[];
  selected: string[];
  onChange: (next: string[]) => void;
  title?: string;
  subtitle?: string;
}

export const AddOnsSelector: React.FC<AddOnsSelectorProps> = ({
  items,
  selected,
  onChange,
  title = 'Optional Add-ons',
  subtitle = 'These services can be added based on your needs. Quote-based items are scoped separately.',
}) => {
  const toggle = (id: string) => {
    const isSelected = selected.includes(id);
    const next = isSelected ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);

    // Track analytics
    const item = items.find((i) => i.id === id);
    if (item) {
      pricingAnalytics.trackAddOnSelection(item.id, item.name, !isSelected, 'montana_pricing_page');
    }
  };

  return (
    <div className="border rounded-xl p-6 bg-white shadow-xs">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        {subtitle && <p className="text-sm text-gray-600 mb-3">{subtitle}</p>}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-700 flex items-center gap-2">
            <Info className="w-4 h-4 shrink-0" />
            <span>
              Your selections will be included in your package proposal and can be adjusted during
              your discovery call.
            </span>
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {items.map((item) => {
          const isSelected = selected.includes(item.id);
          const isQuote = item.pricingModel === 'quote';
          const priceLabel = isQuote
            ? 'Quote-based'
            : item.price !== undefined
              ? item.pricingModel === 'fixed-monthly'
                ? `$${item.price}/month`
                : item.pricingModel === 'fixed-setup'
                  ? `$${item.price} one-time`
                  : item.pricingModel === 'per-parcel-setup'
                    ? `$${item.price}/parcel (one-time)`
                    : ''
              : '';

          return (
            <label
              key={item.id}
              className={`group relative flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-xs'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1.5 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={isSelected}
                onChange={() => toggle(item.id)}
              />

              <div className="flex-1 pr-8">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex flex-col gap-2">
                    <span className="font-semibold text-gray-900 text-base">{item.name}</span>
                    <div className="flex flex-wrap gap-2">
                      {item.badge && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                          {item.badge}
                        </span>
                      )}
                      {item.tags?.includes('montana-recommended') && (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                          Recommended
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900">{priceLabel}</div>
                    <div className="text-xs text-gray-500 capitalize">
                      {item.applicableTiers.join(' • ')}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>

              <div className="absolute top-4 right-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      aria-label={`More details about ${item.name}`}
                      className="inline-flex items-center text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <Info className="w-4 h-4" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-md text-left">
                      <div className="font-medium mb-1">{item.name}</div>
                      <div className="text-sm">{item.description}</div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};
